/* רכיבי בסיס: חלון תחתון, מספר עם הסבר, דיאלוגים, הודעה ופס טעינה */
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { nav, openModal, closeModal } from '../app/nav.js';
import { UI, useApp } from '../store/ui.js';
import { Icon } from './Icon.jsx';

// האם החלון הזה הוא העליון. חלון שמתחת לו מוסתר אבל נשאר מצויר, ולכן לא מגיב ל-Escape
const TopCtx = createContext(true);

export function Sheet({ title, children, onClose = closeModal, wide }) {
  const isTop = useContext(TopCtx);
  useEffect(() => {
    if (!isTop) return undefined;
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose, isTop]);
  return (
    <div className="sheet-ov" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={'sheet' + (wide ? ' wide' : '')} role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined}>
        <div className="sheet-h">
          <b>{title}</b>
          <button className="sheet-x" onClick={onClose} aria-label="סגור"><Icon name="x" size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** כל החלונות הפתוחים. רק העליון גלוי, אבל כולם נשארים מצוירים: כשחוזרים לחלון שמתחת,
    הוא באותו מקום גלילה ובאותו מצב שבו עזבו אותו */
export function ModalHost() {
  useApp();
  const n = nav.stack.length;
  useEffect(() => {
    document.body.style.overflow = n ? 'hidden' : '';
  }, [n]);
  return nav.stack.map((m, i) => (
    <div key={m.id} style={i < n - 1 ? { visibility: 'hidden' } : undefined} aria-hidden={i < n - 1 || undefined}>
      <TopCtx.Provider value={i === n - 1}>{m.render()}</TopCtx.Provider>
    </div>
  ));
}

/* ── מספר לחיץ: פותח "איך זה מחושב" ── */
let whyOpener = null;
export function setWhyOpener(fn) { whyOpener = fn; }
export function openWhy(key, arg) { whyOpener && whyOpener(key, arg); }
export function Xn({ k, arg, children }) {
  const go = (e) => { e.stopPropagation(); openWhy(k, arg); };
  return (
    <span className="xn" role="button" tabIndex={0} onClick={go} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && go(e)}>
      {children}
    </span>
  );
}

/* ── דיאלוגים ── */
function ConfirmSheet({ title, body, okLabel, danger, onOk, onCancel }) {
  return (
    <Sheet title={title} onClose={onCancel}>
      <div className="sub mb12" style={{ fontSize: '.93rem', lineHeight: 1.6 }}>{body}</div>
      <div className="row">
        <button className={'ubtn ' + (danger ? 'ubtn-danger' : 'ubtn-primary')} style={{ flex: 1 }} onClick={() => onOk()}>{okLabel || 'אישור'}</button>
        <button className="ubtn ubtn-ghost" style={{ flex: 1 }} onClick={onCancel}>ביטול</button>
      </div>
    </Sheet>
  );
}
function PromptSheet({ title, body, okLabel, initial, type, onOk, onCancel }) {
  const [v, setV] = useState(initial == null ? '' : String(initial));
  const ref = useRef(null);
  useEffect(() => { setTimeout(() => ref.current && ref.current.focus(), 80); }, []);
  return (
    <Sheet title={title} onClose={onCancel}>
      {body && <div className="sub mb12" style={{ fontSize: '.93rem' }}>{body}</div>}
      <input ref={ref} type={type || 'text'} inputMode={type === 'number' ? 'decimal' : undefined} value={v}
        onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && onOk(v)}
        style={{ textAlign: 'center', marginBottom: 14, direction: type === 'number' ? 'ltr' : undefined }} />
      <div className="row">
        <button className="ubtn ubtn-primary" style={{ flex: 1 }} onClick={() => onOk(v)}>{okLabel || 'אישור'}</button>
        <button className="ubtn ubtn-ghost" style={{ flex: 1 }} onClick={onCancel}>ביטול</button>
      </div>
    </Sheet>
  );
}
function dialog(Comp, opts, cancelValue) {
  return new Promise((resolve) => {
    let done = false;
    const finish = (v) => { if (done) return; done = true; closeModal(); resolve(v); };
    openModal(() => <Comp {...opts} onOk={(v) => finish(v === undefined ? true : v)} onCancel={() => finish(cancelValue)} />, {
      onClose: () => { if (!done) { done = true; resolve(cancelValue); } },
    });
  });
}
/** @returns {Promise<boolean>} */
export function confirmDialog(opts) {
  return dialog(ConfirmSheet, opts, false).then((v) => v === true);
}
/** @returns {Promise<string|null>} */
export function promptDialog(opts) {
  return dialog(PromptSheet, opts, null);
}

export function Toast() {
  useApp();
  const t = UI.toast;
  return <div className={'toast' + (t ? ' on' : '')} role="status" aria-live="polite">{t ? t.msg : ''}</div>;
}
export function LoadBar() {
  useApp();
  return <div className="lbar" style={{ width: UI.loading + '%' }} />;
}

/** כפתורי בחירה (עו"ש/אשראי, הוצאה/הכנסה) */
export function Seg({ value, onChange, options, kind, size }) {
  return (
    <div className={'seg' + (kind ? ' ' + kind : '') + (size ? ' ' + size : '')} role="radiogroup">
      {options.map(([v, label, cls]) => (
        <button type="button" role="radio" aria-checked={value === v} key={v} className={(value === v ? 'on ' : '') + (cls || '')} onClick={() => onChange(v)}>{label}</button>
      ))}
    </div>
  );
}

export function Empty({ emo, children }) {
  return (
    <div className="empty">
      {emo && <div className="emo">{emo}</div>}
      <p>{children}</p>
    </div>
  );
}
